import { ApolloClient } from "apollo-boost"
import { filterFields } from "fgr-lib-business-logic/lib/common/utils/filterFields"
import { NextComponentType } from "next"
import { Router as RouterProps, withRouter } from "next/router"
import * as React from "react"
import { compose } from "react-apollo"
import logger from "../../server/logger"
import { TranslateSlugsQueryDocument } from "../../graphql/generated-types"
import { AuthInfoContext, AuthInfoContextInterface } from "../context/AuthContext"
import { withContext } from "../context/utils"
import withApolloClient from "./withApolloClient"

interface Props {
    apolloClient: ApolloClient<any>
    authInfoContext: AuthInfoContextInterface
    router: RouterProps
}

export interface LoggerObj {
    info: (message, data?) => void
    warn: (message, data?) => void
    error: (message, data?) => void
}

interface WrappedComponentProps {
    logger: LoggerObj
}

const withLogger = <P extends WrappedComponentProps>(WrappedComponent: NextComponentType<P>) => {
    class WithLoggerHOC extends React.Component<P & Props> {
        static getInitialProps
        translateSlugs = (callback: (accountId, locationId, terminalId) => void) => {
            return this.props.apolloClient
                .query({
                    query: TranslateSlugsQueryDocument,
                    variables: {},
                    fetchPolicy: "cache-first",
                })
                .then((result) => {
                    const accountId = result.data && result.data.translateSlugs && result.data.translateSlugs.accountId
                    const locationId =
                        result.data && result.data.translateSlugs && result.data.translateSlugs.locationId
                    const terminalId =
                        result.data && result.data.translateSlugs && result.data.translateSlugs.terminalId
                    return callback(accountId, locationId, terminalId)
                })
                .catch((error) => {
                    logger.warn("Error in TranslateSlugsQuery::withLogger", { error: error })
                    return callback(null, null, null)
                })
        }

        log = (level, message, data) => {
            this.translateSlugs((accountId, locationId, terminalId) => {
                logger.log(
                    level,
                    message,
                    filterFields(
                        [
                            "session",
                            "twbrds-session",
                            "fgr-service-token",
                            "password",
                            "cc_signature",
                            "signature",
                            "token",
                            "cvv",
                            "expiration",
                        ],
                        data,
                        null,
                        0
                    ),
                    {
                        account_id: accountId,
                        location_id: locationId,
                        terminal_id: terminalId,
                        browser_info: window && navigator ? navigator.userAgent : null,
                        hotjar_user_id: this.props.authInfoContext.hotjarUserId,
                    }
                )
            })
        }

        loggerInfo = (message, data) => this.log("info", message, data)
        loggerWarn = (message, data) => this.log("warn", message, data)
        loggerError = (message, data) => this.log("error", message, data)

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    logger={{
                        info: this.loggerInfo,
                        warn: this.loggerWarn,
                        error: this.loggerError,
                    }}
                />
            )
        }
    }

    if (WrappedComponent.getInitialProps) {
        WithLoggerHOC.getInitialProps = WrappedComponent.getInitialProps
    }

    return WithLoggerHOC
}

export default compose(
    withApolloClient,
    withContext([AuthInfoContext], (context: AuthInfoContextInterface) => ({ authInfoContext: context })),
    withRouter,
    withLogger
)
